import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { KeyboardArrowDown } from '@mui/icons-material';
import styled from '@emotion/styled';
import { uiTheme as t } from './theme';

export interface FilterOption {
  value: string;
  label: string;
}

interface FilterSelectProps {
  /** Valores seleccionados. El arreglo vacío significa "sin filtro". */
  value: string[];
  options: FilterOption[];
  /** Texto que se muestra cuando no hay filtro aplicado. */
  placeholder: string;
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

const OptionLabel = styled.span({
  fontFamily: t.font,
  fontSize: 14,
  color: t.color.title,
});

/**
 * Molécula: filtro desplegable de selección múltiple del lenguaje de
 * Configuración.
 *
 * Se apoya en el `Select` de MUI —ya instalado— en vez de un `<select multiple>`
 * nativo: el nativo deja de pintarse como desplegable y se convierte en una
 * caja de lista siempre abierta, que rompería la línea de la barra de filtros.
 * MUI trae resueltos el teclado, el clic fuera y los lectores de pantalla.
 *
 * La caja es el propio `Select`, no un div que lo envuelve. Con envoltorio, lo
 * único que abría el menú era el `div[role="button"]` de dentro, que medía el
 * alto del texto: el borde, el relleno y la zona del icono quedaban muertos al
 * clic. Aquí el borde y el relleno viven en el control, así que los 44px de
 * alto por todo el ancho abren el menú.
 */
const FilterSelect = ({ value, options, placeholder, onChange, disabled = false }: FilterSelectProps) => {
  const labelOf = (id: string) => options.find(option => option.value === id)?.label ?? id;
  const active = value.length > 0;

  return (
    <Select<string[]>
      multiple
      displayEmpty
      variant="standard"
      value={value}
      disabled={disabled}
      onChange={event => {
        const next = event.target.value;
        onChange(typeof next === 'string' ? next.split(',') : next);
      }}
      IconComponent={KeyboardArrowDown}
      inputProps={{ 'aria-label': placeholder }}
      renderValue={selected =>
        selected.length === 0 ? (
          <OptionLabel style={{ color: t.color.muted }}>{placeholder}</OptionLabel>
        ) : (
          <OptionLabel>{selected.map(labelOf).join(', ')}</OptionLabel>
        )
      }
      MenuProps={{
        PaperProps: {
          sx: {
            marginTop: '6px',
            borderRadius: '11px',
            boxShadow: '0 12px 30px rgba(16, 24, 40, 0.16)',
            maxHeight: 320,
          },
        },
      }}
      sx={{
        // La caja.
        height: 44,
        minWidth: 168,
        maxWidth: 260,
        alignItems: 'stretch',
        background: disabled ? t.color.disabledSurface : t.color.surface,
        border: `1px solid ${active ? t.color.primary : t.color.inputBorder}`,
        borderRadius: '11px',
        fontFamily: t.font,
        fontSize: 14,
        transition: 'border-color 0.15s ease',
        '&:focus-within': { borderColor: t.color.primary },
        // Las líneas del variante "standard" sobran: el borde ya lo pone la caja.
        '&::before, &::after': { display: 'none' },
        // El div con role="button" es lo único que abre el menú: tiene que
        // ocupar la caja entera, relleno incluido.
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          height: '100%',
          boxSizing: 'border-box',
          padding: '0 12px',
          paddingRight: '34px !important',
          background: 'transparent',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        '& .MuiSelect-icon': { right: 10, fontSize: 20, color: t.color.muted },
      }}
    >
      {options.map(option => (
        <MenuItem key={option.value} value={option.value} sx={{ fontFamily: t.font, fontSize: 14 }}>
          <Checkbox
            size="small"
            checked={value.includes(option.value)}
            sx={{ padding: '4px 8px 4px 0', color: t.color.inputBorder, '&.Mui-checked': { color: t.color.primary } }}
          />
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
};

export default FilterSelect;
